import * as tools from '../utils/tools';
import {cache} from '../cache/cache';
import { View } from './viewApi';
import { Record } from './recordApi';
import { Value } from './valueApi';
import { UI } from './uiAPI';
import { Utils } from './utilsAPI';
import { UserApi } from './userApi';
import * as actionHelpers from './actionHelpers';
import * as expressionHelpers from './expressionHelpers';
import { EntityApi } from "./EntityApi";
import { VariableApi } from "./variableApi";

const variablePrefix = "dvVar_";
const userVariablePefix = "dvUserVal_";

class OpenViews {
    constructor (openViews) {
        this._openViews = openViews;
    }

    /**
     * @returns {Object} Returns a dictionary of all open top-level views.
     */
    allOpenViews () {
        console.warn("DEPRECATED: api.openViews, please use api.openForms");
        return this._openViews.Values;
    }

    /**
     * @returns {Object} Returns the first top-level view in the list of open views.
     */
    first () {
        console.warn("DEPRECATED: api.openViews, please use api.openForms");
        return this._openViews.Values.first();
    }

    /**
     * @returns {Object} Returns the last top-level view in the list of open views.
     */
    last () {
        console.warn("DEPRECATED: api.openViews, please use api.openForms");
        return this._openViews.Values.last();
    }

    /**
     * @param {Number} id - The view ID requested.
     * @returns {Object} Returns the view that matches the view ID specified from the list of open views.
     */
    findByID (id) {
        console.warn("DEPRECATED: api.openViews, please use api.openForms");
        return this._openViews.getVal(id);
    }

    /**
     * @param {String} uid - The view UID requested.
     * @returns {Object} Returns the view that matches the view UID specified from the list of open views.
     */
    findByUID (uid) {
        console.warn("DEPRECATED: api.openViews, please use api.openForms");
        if (uid == null || typeof uid !== 'string') return null;
        //Otherwise search the list of open views...EK
        return this._openViews.Values.find(v => v.raw.drivingView.UID.toLowerCase() === uid.toLowerCase());
    }

    /**
     * @param {Object} linker - The view linker object to search for.
     * @returns {Object} Returns the view that matches the linker object specified from the list of open views.
     */
    findByLinker (linker) {
        console.warn("DEPRECATED: api.openViews, please use api.openForms");
        return this._openViews.getVal(linker);
    }

    /**
     * @param {String} name - The name of the view to search for.
     * @returns {Object} Returns the view that matches the name specified from the list of open views.
     */
    findByName (name) {
        console.warn("DEPRECATED: api.openViews, please use api.openForms");
        if (name == null || typeof name !== 'string') return null;
        //Otherwise search the list of open views...EK
        return this._openViews.Values.find(v => v.name.toLowerCase() === name.toLowerCase());
    }

    /**
     * @returns {Object} Returns the currently focused top-level (windowed) view.
     */
    get current () {
        // Get the active window, and return it's view object, if any..PW
        console.warn("DEPRECATED: api.openViews.current, please use api.openForms.active to get the active form.");
        let activeWindow = app.dv.userWorkspace.windowManager.activeWindow;
        if (activeWindow == null) {
            return null;
        }

        // Return the view's mvc object if it exists, if not, return null..PW
        return activeWindow.mvc.apiRef;
    }
}

class OpenForms {
    constructor (openViews) {
        this._openForms = openViews;
    }

    /**
     * @returns {Object} Returns a dictionary of all open top-level forms.
     */
    allOpenForms () {
        return this._openForms.Values;
    }

    /**
     * @returns {Object} Returns the first top-level form in the list of open forms.
     */
    first () {
        return this._openForms.Values.first();
    }

    /**
     * @returns {Object} Returns the last top-level form in the list of open forms.
     */
    last () {
        return this._openForms.Values.last();
    }

    /**
     * @param {Number} id - The form ID requested.
     * @returns {Object} Returns the form that matches the form ID specified from the list of open forms.
     */
    findByID (id) {
        return this._openForms.getVal(id);
    }

    /**
     * @param {String} uid - The form UID requested.
     * @returns {Object} Returns the form that matches the form UID specified from the list of open forms.
     */
    findByUID (uid) {
        if (uid == null || typeof uid !== 'string') return null;
        //Otherwise search the list of open forms...PW
        return this._openForms.Values.find(v => v.raw.drivingView.UID.toLowerCase() === uid.toLowerCase());
    }

    /**
     * @param {Object} linker - The view linker object to search for.
     * @returns {Object} Returns the view that matches the linker object specified from the list of open views.
     */
    findByLinker (linker) {
        return this._openForms.getVal(linker);
    }

    /**
     * @param {String} name - The name of the form to search for.
     * @returns {Object} Returns the form that matches the name specified from the list of open forms.
     */
    findByName (name) {
        if (name == null || typeof name !== 'string') return null;
        //Otherwise search the list of open forms...EK
        return this._openForms.Values.find(v => v.name.toLowerCase() === name.toLowerCase());
    }

    /**
     * @returns {Object} Returns the currently focused top-level (windowed) view.
     */
    get active () {
        // Get the active window, and return it's view object, if any..PW
        let activeWindow = app.dv.userWorkspace.windowManager.activeWindow;
        if (activeWindow == null) {
            return null;
        }

        // Return the view mvc object if it exists, if not, return null..PW
        return activeWindow.mvc.apiRef;
    }
}

class ApiEnvironment {
    constructor () {
        /**
         * Object to contain properties and values that correspond to a URL query string.
         * @type {Object}
         */
        this.queryStringParams = {};
    }

    /**
     * Method to parse the specified query string into the api.environment.queryString object.
     * @param {String} queryString - The query string to parse to create the api.environment.queryString object.
     */
    setQueryStringObject (queryString) {
        // clear to a blank object
        this.queryStringParams = {};

        // parse the string and populate the object.
        if (queryString && queryString !== "undefined") {
            queryString.replace(
                new RegExp("([^?=&]+)(=([^&]*))?", "g"),
                ($0, $1, $2, $3) => {this.queryStringParams[decodeURIComponent($1)] = decodeURIComponent($3);}
            );
        }
    }

    /**
     * Initialize environment variables from app session cache, and keep them in sync.
     * @param {Dictionary} variables - The cached variables dictionary.
     */
    initializeVariables (variables) {
        // Create placeholder for environment's variables, and alias the raw variables dictionary for internal use
        this.variables = {};
        this.variables._raw = variables;

        // Watch for variables to be stored and add them to the API.
        this.variables._raw.on("add", (item) => {
            this._normalizeToApi(item.key, item.value);
        });

        // Watch for variables to be removed and remove them from the API.
        this.variables._raw.on("remove", (item) => {
            if (item.key) {
                const friendlyKey = this.getFriendlyVariableName(item.key);
                if (friendlyKey) {
                    delete this.variables[friendlyKey];
                }
            }
        });

        // when the dictionary is cleared, remove all variables.
        this.variables._raw.on('clear', (clearedKeys) => {
            clearedKeys.forEach((key) => {
                if (key) {
                    const friendlyKey = this.getFriendlyVariableName(key);
                    if (friendlyKey) {
                        delete this.variables[friendlyKey];
                    }
                }
            })
        });

        // Map variables to objects on api.environment.variables.
        this.variables._raw.Keys.forEach((key) => {
            const variable = this.variables._raw.getVal(key);
            if (variable) {
                this._normalizeToApi(key, variable);
            }
        });
    }

    /**
     * Generates a fully qualified variable name from a base name which may or may not already include the standard variable prefix.
     * @param  {String} baseName - The base variable name.
     * @returns {String} The fully qualified variable name (e.g. "{dvVar_base.name}").
     */
    getFullVariableName (baseName) {
        if (!baseName) return null;

        let name = baseName;
        // Determine if the standard prefix is already present (may be after an opening brace)
        const isPrefixed = baseName.match(/^{?(dvVar_|dvUserVal_)/);
        if (!isPrefixed) {
            // Add the prefix if it's not present at the beginning of the string
            name = `${variablePrefix}${baseName}`;
        }
        // Add enclosing braces if not present
        if (!name.startsWith("{")) {
            name = `{${name}`;
        }
        if (!name.endsWith("}")) {
            name = `${name}}`;
        }
        return name;
    }

    /**
     * Gets the variable's friendly name from a variable object or its fully qualified name.
     * @param {String|VariableApi|app.dv.entities.user.infoVariable} variable - The variable object or fully qualified variable name as a string.
     * @returns {String} The variable's friendly name.
     */
    getFriendlyVariableName (variable) {
        if (variable instanceof VariableApi) {
            variable = variable.raw;
        }
        const name = variable.FilterString || variable;
        return name.replace(/^{?dvVar_/, "").replace(/^{?dvUserVal_/, "User_").replace(/}$/, "");
    }

    /**
     * Gets a stored variable by name, optionally creating the variable and assigning a default value if it does not already exist.
     * @param {String} name - The variable name (may or may not already include the standard variable prefix).
     * @param {app.dv.types.PropertyDataType} [propertyDataType] - The data type of the variable (e.g. app.dv.types.PropertyDataType.Text).
     *                                                             Pass or leave the default undefined to NOT create a variable in the case it does not already exist.
     * @param {*} [defaultValue] - The default value to store if desired. Pass or leave the default undefined to NOT assign anything.
     * @returns {VariableApi} An API-wrapped stored variable or null if not found.
     */
    getVariable (name, propertyDataType, defaultValue) {
        if (!name) return null;

        const variableName = api.environment.getFullVariableName(name);
        let storedVariable = app.dv.cache.session.variables.getVal(name);
        if (!storedVariable) {
            storedVariable = app.dv.cache.session.variables.Values.find((storedVar) => {
                return (storedVar.FilterString === variableName);
            });
        }

        // Create the user variable if it doesn't already exist
        if (!storedVariable && propertyDataType !== undefined) {
            storedVariable = new app.dv.entities.user.infoVariable(variableName, new app.dv.entities.dynamicValue.DynamicValue(defaultValue));
            app.dv.cache.session.variables.add(variableName, storedVariable);
        }

        // Set the data type
        if (storedVariable && storedVariable.InfoValue && storedVariable.InfoValue.DataType !== propertyDataType) {
            storedVariable.InfoValue.DataType = propertyDataType;
        }

        // Assign default value, if any is specified
        if (storedVariable && defaultValue !== undefined) {
            storedVariable.InfoValue.Value = defaultValue;
        }

        // Return an API-wrapped variable instance
        return (storedVariable != null) ? new VariableApi(storedVariable) : null;
    }

    /**
     * Get the value of a stored variable by name.
     * @param {String} name - The variable name (may or may not already include the standard variable prefix).
     * @returns {Object} The currently stored value of the variable or null if not found.
     */
    getVariableValue (name) {
        const variable = api.environment.getVariable(name, undefined);
        return variable && variable.raw && variable.raw.InfoValue ? variable.raw.InfoValue.Value : null;
    }

    /**
     * Sets a stored variable by name, optionally creating the variable and assigning a default value if it does not already exist.
     * @param {String} name - The variable name (may or may not already include the standard variable prefix).
     * @param {*} value - The value to store.
     * @param {app.dv.types.PropertyDataType} [propertyDataType] - The data type of the variable (defaults to app.dv.types.PropertyDataType.Text).
     *                                                               Pass undefined to NOT create a variable in the case it does not already exist.
     * @returns {VariableApi} An API-wrapped stored variable or null if not found.
     */
    setVariableValue (name, value, propertyDataType = app.dv.types.PropertyDataType.Text) {
        const variable = api.environment.getVariable(name, propertyDataType, value);
        return variable;
    }

    /**
     * Removes a stored variable.
     * @param {String} name - The variable name (may or may not already include the standard variable prefix) to be removed.
     */
    removeVariable (name) {
        app.dv.cache.session.variables.remove(api.environment.getFullVariableName(name));
    }

    /**
     * Map a single stored variable to api.environment.variables.
     * @param {String} key - The unique key to the variable.
     * @param {VariableApi|app.dv.entities.user.infoVariable} variable - The variable value.
     */
    _normalizeToApi (key, variable) {
        if (variable instanceof VariableApi) {
            variable = variable.raw;
        }
        if (key && variable.InfoValue) {
            const friendlyKey = api.environment.getFriendlyVariableName(key);
            if (friendlyKey) {
                Object.defineProperty(api.environment.variables, friendlyKey, {
                    get: function() {
                        return api.environment.getVariableValue(key);
                    },
                    set: function(value) {
                        api.environment.setVariableValue(key, value);
                    },
                    enumerable: true,
                    configurable: true,
                });
            }
        }
    }
}

class BuildAPI {
    constructor() {
        /**
         * The current framework version
         * @type {String|null}
         */
        this.version = null;

        /**
         * The tag this environment is currently on
         * @type {String|null}
         */
        this.tag = null;

        /**
         * The datetime when the build completed that the server is running on
         * @type {Date|null}
         */
        this.date = null;

        /**
         * The commit hash that shows what the last commit in the build was
         * @type {String|null}
         */
        this.hash = null;
    }

    updateFromConfig() {
        this.version = app.dv.cache.session.config.version;
        this.hash = app.dv.cache.session.config.buildHash;
        this.tag = app.dv.cache.session.config.buildTag;

        this.date = new Date(app.dv.cache.session.config.buildDate);
        if (this.date.toString() === 'Invalid Date' || !app.dv.cache.session.config.buildDate) {
            this.date = null;
        }
    }
}

export default class API {
    constructor() {
        /**
         * Object that should be used for custom logic to prevent breaking the core API
         * @type {Object}
         */
        this.runtime = {};

        this._openViews = new tools.Dictionary();

        this.openViews = new OpenViews(this._openViews);

        this.openForms = new OpenForms(this._openViews);

        /**
         * The factory is where we keep our constructor functions for creating API layer objects...EK
         * @type {{View: View, Record: Record, Value: Value, UI: UI, Utils: Utils}}
         */
        this.factory = {
            View,
            Record,
            Value,
            UI,
            Utils
        };

        this.environment = new ApiEnvironment();

        /**
         * Exposes useful information about the current build being run on
         * @type {BuildAPI}
         */
        this.build = new BuildAPI();

        /**
         * define debug mode variables.
         * @type {boolean}
         * @private
         */
        this._debugMode = false;

        /**
         * define debug mode variables.
         * @type {boolean}
         * @private
         */
        this._debugModeExpanded = false;

        /**
         * Expose the UI helper API
         */
        this.ui = new UI();

        /**
         * Expose the current user API
         */
        this.user = new UserApi();

        /**
         * Expose the current user API
         */
        this.entity = new EntityApi();

        /**
         * Expose the Utils helper API
         */
        this.utils = new Utils();

        /**
         * Expose the action helpers
         */
        this.actionHelpers = actionHelpers;

        /**
         * Expose the expression helpers
         */
        this.expressionHelpers = expressionHelpers;

        // Listen for newly created views and bind the API to them...EK
        cache.mvcs.on('add', (addedView) => {
            this._openViews.add(addedView.key, new View(addedView.value, null), addedView.UID);
        });

        // Listen for removed views and destroy the API reference to them...EK
        cache.mvcs.on('remove', (removedView) => {
            let apiView = this._openViews.getVal(removedView.key);
            if (apiView != null) {
                apiView.dispose();
            }
            this._openViews.remove(removedView.key);
        });

        // Now that variables have been cached, sync them with the environment object
        this.environment.initializeVariables(cache.session.variables);
    }

    debugMode(debuggerOn, expanded) {
        //Handle when nothing is passed into the debug Mode...;
        this._debugMode = (debuggerOn == null || debuggerOn == true);

        //Set the debug mode to be expanded...eg
        //if _debugMode is false then automatically set _debugModeExpanded to false
        this._debugModeExpanded = (expanded == true && this._debugMode);

        return this._debugMode ? "Debugging On" : "Debugging Off";
    }

    /**
     * Determines if the browser is running on a mobile device (in browser or as a native hybrid Cordova-wrapped app).
     * @returns {boolean} True if the app is running on a mobile device.
     */
    isMobileDevice() {
        if (this._isMobile != null) {
            return this._isMobile;
        }

        // Checks the browser type and returns true if the browser is a mobile device.
        this._isMobile = (navigator.userAgent.match('Android') != null
            || navigator.userAgent.match('webOS') != null
            || navigator.userAgent.match('iPhone') != null
            || navigator.userAgent.match('iPad') != null
            || navigator.userAgent.match('iPod') != null
            || navigator.userAgent.match('BlackBerry') != null
            || navigator.userAgent.match('Windows Phone') != null
            || navigator.userAgent.match('Mobile') != null
        );

        return this._isMobile;
    }

    /**
     * Expose api expressions
     * @returns {Object} Returns the references to the list of Expression Functions.
     */
    get expressions() {
        // Return the direct reference to the list of registered expressions..PW
        return app.dv.helpers.exprEng.expr;
    }

    /**
     * Determines if the app is running on a mobile device as a native (hybrid Cordova-wrapped) app.
     */
    get isNativeApp () {
        return window.isNativeApp;
    }
}
